import { t } from 'i18next';
import moment from 'moment';
import { path, propOr, mergeDeepLeft, equals } from 'ramda';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { authActions } from '../../../modules/auth';
import { getPlanTierLabel } from '../../../utils/helpers/getPlanTier';
import useDetectDiffInChanges from '../../../utils/hooks/useDetectDiffInChanges';
import { useForm } from '../../../utils/hooks/useForm';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import VerifiedIcon from '../../assets/imgs/icons/Verified_Icon.svg';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { Icon, InputLabel, SecondaryBtnS, Text, TextInput, LinkTag } from '../common';
import { ChangePassModal, ResetPassModal } from '../modals';

import FieldComment from './common/fieldComment';
import SaveBtn from './common/saveBtn';
import {
  CompanyHistory,
  CompanyHistoryRecord,
  CompanyHistoryDate,
  CompanyHistoryTitle,
  CompanyVerifiedRow,
  InputWrapper,
  MainSectionS,
  CompanyInfoRowEditS,
  PasswordButtonsWrapper,
  PlanButtonsWrapper,
  SaveButtonWrapper,
} from './companyEditS';
import CompanyPhotoUploader from './companyPhotoUploader';

const validationSchema = Yup.object().shape({
  name: Yup.string().required(t('companyEdit.errorMessages.name')),
  base: Yup.object().shape({
    email: Yup.string()
      .required(t('companyEdit.errorMessages.email_required'))
      .email(t('companyEdit.errorMessages.email')),
  }),
});

const getInitialValues = ({ company, isInitialCompany }) => {
  if (isInitialCompany) {
    const password = Math.random().toString(36).slice(-8);
    return {
      name: propOr('', 'name')(company),
      base: { email: path(['base', 'email'], company) || '' },
      password,
      confirmPassword: password,
    };
  }
  return {
    name: propOr('', 'name')(company),
    base: { email: path(['base', 'email'], company) || '' },
  };
};

const CompanyHeader = ({ company, windowWidth, isAdmin }) => (
  <DisplayFlexS justifyScreenMedium="center" style={{ marginBottom: 20 }}>
    <DisplayFlexS flexWrapScreenTablet="wrap" justifyScreenMedium="center">
      <DisplayFlexS maxWidth="46%">
        <CompanyPhotoUploader company={company} isAdmin={isAdmin} />
        <DisplayFlexS
          direction="column"
          alignItems="center"
          directionScreenMedium="column"
          style={{ margin: 'auto 0', width: 'auto' }}
        >
          <CompanyInfoRowEditS>
            <Text color="darkerViolet" fontFamily="bold">
              {company.name}
            </Text>
          </CompanyInfoRowEditS>
          {company.verified && (
            <CompanyVerifiedRow>
              <Icon src={VerifiedIcon} />
              <Text>
                <Trans i18nKey="companyItem.verified" />
              </Text>
            </CompanyVerifiedRow>
          )}
        </DisplayFlexS>
      </DisplayFlexS>
      {windowWidth > SCREEN_SIZES.MEDIUM && <FieldComment transPath="companyEdit.fieldComment.settingsLogo" />}
    </DisplayFlexS>
  </DisplayFlexS>
);

const CompanyHistorySection = ({ company }) => (
  <CompanyHistory>
    <CompanyHistoryRecord>
      <CompanyHistoryTitle weight="bold">
        <Trans i18nKey="adminPanel.companies.createDate" />
      </CompanyHistoryTitle>
      <CompanyHistoryDate>
        {`${moment(company.createdAt).format('HH:mm, D MMM YYYY')} ${t('adminPanel.companies.from')} ${company.name}`}
      </CompanyHistoryDate>
    </CompanyHistoryRecord>
  </CompanyHistory>
);

const InputWrapperSection = ({ label, name, value, onChange, onBlur, errors, touched }) => {
  const error = path(name.split('.'), errors);
  const isTouched = path(name.split('.'), touched);
  const isError = !!(error && isTouched);

  return (
    <DisplayFlexS justifyScreenMedium="center" direction="column" directionScreenMedium="column" maxWidth="46%">
      <InputWrapper>
        <TextInput
          label={label}
          name={name}
          type="text"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isError={isError}
          error={error}
        />
      </InputWrapper>
    </DisplayFlexS>
  );
};

const PasswordSection = ({ windowWidth, toggleChangePass, toggleResetPass, isAdmin }) => (
  <DisplayFlexS
    justifyScreenMedium="center"
    direction="column"
    directionScreenMedium="column"
    style={{ marginBottom: 10 }}
  >
    <PasswordButtonsWrapper>
      {windowWidth < SCREEN_SIZES.MEDIUM && (
        <InputLabel>
          <Trans i18nKey="profile.labels.password" />
        </InputLabel>
      )}
      <SecondaryBtnS type="button" padding="11px 30px" transform="none" onClick={() => toggleChangePass(true)}>
        <Trans i18nKey="profile.button.changePassword" />
      </SecondaryBtnS>
      {isAdmin && (
        <SecondaryBtnS type="button" padding="11px 11px" transform="none" onClick={() => toggleResetPass(true)}>
          <Trans i18nKey="profile.button.resetPassword" />
        </SecondaryBtnS>
      )}
    </PasswordButtonsWrapper>
  </DisplayFlexS>
);

const MainSection = ({ isAdmin, isInitialCompany, updateCompany, setActiveSettingType }) => {
  const [isChangePassOpen, setIsChangePassOpen] = useState(false);
  const [isResetPassOpen, setIsResetPassOpen] = useState(false);
  const dispatch = useDispatch();

  const company = useSelector((state) => state.company.companyItem);
  const loading = useSelector((state) => state.company.loading);
  const { width: windowWidth } = useWindowSize();

  const initialValues = getInitialValues({ company, isInitialCompany });

  const handleSubmit = (values) => {
    if (isInitialCompany) {
      const payload = { ...values, email: values.base.email, isInitialCompany };
      dispatch(authActions.signUp({ values: payload, withNotification: true }));
    } else {
      dispatch(updateCompany(mergeDeepLeft(values, company), isAdmin));
    }
  };

  const hasChanges = (values) => !equals(initialValues, values);

  const {
    values,
    errors,
    touched,
    onChange: handleChange,
    onBlur: handleBlur,
    onSubmit,
    dirty,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useDetectDiffInChanges(() => hasChanges(values), onSubmit, setActiveSettingType, values);

  return (
    <>
      <ChangePassModal
        isModalOpen={isChangePassOpen}
        toggleModal={setIsChangePassOpen}
        additionalDataToSend={{ companyId: company.id }}
      />
      <ResetPassModal isModalOpen={isResetPassOpen} toggleModal={setIsResetPassOpen} />

      <DisplayFlexS direction="column" directionScreenMedium="column">
        <CompanyHeader company={company} windowWidth={windowWidth} isAdmin={isAdmin} />

        {isAdmin && <CompanyHistorySection company={company} />}

        <MainSectionS isAdmin={isAdmin}>
          <form onSubmit={onSubmit}>
            <DisplayFlexS
              justifyScreenMedium="center"
              maxWidth="46%"
              margin="0 0 20px 0"
              marginScreenMedium="0 0 15px 0"
            >
              <InputWrapper>
                <TextInput
                  label={t('companyEdit.labels.name')}
                  name="name"
                  type="text"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isError={path(['name'], errors) && path(['name'], touched)}
                  error={path(['name'], errors)}
                />
              </InputWrapper>
            </DisplayFlexS>

            <DisplayFlexS flexWrapScreenTablet="wrap" justifyScreenMedium="center">
              <InputWrapperSection
                label={t('companyEdit.labels.email')}
                name="base.email"
                value={values.base.email}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                touched={touched}
              />
              {windowWidth > SCREEN_SIZES.MEDIUM && <FieldComment transPath="companyEdit.fieldComment.settingsEmail" />}
            </DisplayFlexS>

            {!isInitialCompany && (
              <PasswordSection
                windowWidth={windowWidth}
                toggleChangePass={setIsChangePassOpen}
                toggleResetPass={setIsResetPassOpen}
                isAdmin={isAdmin}
              />
            )}

            {!isInitialCompany && (
              <>
                <DisplayFlexS 
                  justifyScreenMedium="center"
                  maxWidth="46%"
                  margin="0 0 20px 0"
                  marginScreenMedium="15px 0 0 0"
                >
                  <InputWrapper>
                    <TextInput
                      label={t('companyEdit.labels.planTier')}
                      name="planTier"
                      type="text"
                      value={getPlanTierLabel(company.planTier)}
                      disabled={true}
                      isError={path(['planTier'], errors)}
                      error={path(['planTier'], errors)}
                    />
                  </InputWrapper>
                </DisplayFlexS>

                <DisplayFlexS justifyScreenMedium="center" direction="column" directionScreenMedium="column">
                  <PlanButtonsWrapper>
                    <LinkTag 
                      href="https://treuhand-suche.ch/blog/abos/" 
                      content={
                      <SecondaryBtnS type="button" padding="11px 30px" transform="none">
                        <Trans i18nKey="profile.labels.planTier" />
                      </SecondaryBtnS>
                    }>
                    </LinkTag>
                  </PlanButtonsWrapper>
                </DisplayFlexS>
              </>
            )}

            <SaveButtonWrapper marginTop="30" marginTopMobile="30">
              <SaveBtn loading={loading} disabled={!dirty && !hasChanges(values)} />
            </SaveButtonWrapper>
          </form>
        </MainSectionS>
      </DisplayFlexS>
    </>
  );
};

export default MainSection;
